import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { Button } from '../components/shared/Button';
import './ScoreEntry.css';

interface TeamSongScore {
  yearCorrect: boolean;
  artistCorrect: boolean;
  titleCorrect: boolean;
}

interface RoundScores {
  [teamId: string]: {
    [songId: string]: TeamSongScore;
  };
}

const ScoreEntry: React.FC = () => {
  const navigate = useNavigate();
  const { 
    teams, 
    playlist,
    roundNumber,
    updateSongAnswer,
    updateTeam,
  } = useGame();

  // Get played songs for this round
  const playedSongs = useMemo(() => 
    playlist.filter(song => song.played), 
    [playlist]
  );

  // Local state for scores before saving
  const [scores, setScores] = useState<RoundScores>(() => {
    const initial: RoundScores = {};
    teams.forEach(team => {
      initial[team.id] = {};
      playedSongs.forEach(song => {
        initial[team.id][song.id] = {
          yearCorrect: false,
          artistCorrect: false,
          titleCorrect: false,
        };
      });
    });
    return initial;
  });

  // Current team being scored
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0);
  const currentTeam = teams[currentTeamIndex];

  // Toggle a score checkbox
  const toggleScore = (teamId: string, songId: string, field: keyof TeamSongScore) => {
    setScores(prev => ({
      ...prev,
      [teamId]: {
        ...prev[teamId],
        [songId]: {
          ...prev[teamId][songId],
          [field]: !prev[teamId][songId][field],
        },
      },
    }));
  };

  // Quick toggle all correct for a song
  const toggleAllCorrect = (teamId: string, songId: string) => {
    const current = scores[teamId][songId];
    const allCorrect = current.yearCorrect && current.artistCorrect && current.titleCorrect;
    
    setScores(prev => ({
      ...prev,
      [teamId]: {
        ...prev[teamId],
        [songId]: {
          yearCorrect: !allCorrect,
          artistCorrect: !allCorrect,
          titleCorrect: !allCorrect,
        },
      },
    }));
  };

  // Calculate team's total points
  const calculateTeamPoints = (teamId: string): number => {
    const teamScores = scores[teamId];
    if (!teamScores) return 0;
    
    let points = 0;
    Object.values(teamScores).forEach(songScore => {
      if (songScore.yearCorrect) points++;
      if (songScore.artistCorrect) points++;
      if (songScore.titleCorrect) points++;
    });
    return points;
  };

  // Save all scores and navigate back
  const handleSave = () => {
    // Save all scores to game context and update team scores
    Object.entries(scores).forEach(([teamId, teamScores]) => {
      // Calculate points for this team
      let pointsEarned = 0;
      
      Object.entries(teamScores).forEach(([songId, songScore]) => {
        // Save the answer
        updateSongAnswer(teamId, songId, {
          yearCorrect: songScore.yearCorrect,
          artistCorrect: songScore.artistCorrect,
          titleCorrect: songScore.titleCorrect,
        });
        
        // Count points
        if (songScore.yearCorrect) pointsEarned++;
        if (songScore.artistCorrect) pointsEarned++;
        if (songScore.titleCorrect) pointsEarned++;
      });
      
      // Update team's total score
      const team = teams.find(t => t.id === teamId);
      if (team) {
        updateTeam(teamId, { score: team.score + pointsEarned });
      }
    });
    
    navigate('/admin');
  };

  // Navigate between teams
  const nextTeam = () => {
    if (currentTeamIndex < teams.length - 1) {
      setCurrentTeamIndex(currentTeamIndex + 1);
    }
  };

  const prevTeam = () => {
    if (currentTeamIndex > 0) {
      setCurrentTeamIndex(currentTeamIndex - 1);
    }
  };

  if (teams.length === 0) {
    return (
      <div className="score-entry-page">
        <div className="score-entry-container">
          <h1>📊 Score Invoer</h1>
          <p>Er zijn nog geen teams. Ga naar Setup om teams toe te voegen.</p>
          <Button onClick={() => navigate('/setup')}>Ga naar Setup</Button>
        </div>
      </div>
    );
  }

  if (playedSongs.length === 0) {
    return (
      <div className="score-entry-page">
        <div className="score-entry-container">
          <h1>📊 Score Invoer</h1>
          <p>Er zijn nog geen nummers gespeeld. Start eerst een ronde vanuit Admin.</p>
          <Button onClick={() => navigate('/admin')}>Ga naar Admin</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="score-entry-page">
      <div className="score-entry-container">
        <header className="score-entry-header">
          <h1>📊 Score Invoer - Ronde {roundNumber}</h1>
          <div className="team-nav">
            <Button 
              variant="secondary" 
              onClick={prevTeam}
              disabled={currentTeamIndex === 0}
            >
              ← Vorig Team
            </Button>
            <span className="team-indicator">
              Team {currentTeamIndex + 1} van {teams.length}
            </span>
            <Button 
              variant="secondary" 
              onClick={nextTeam}
              disabled={currentTeamIndex === teams.length - 1}
            >
              Volgend Team →
            </Button>
          </div>
        </header>

        {/* Current team scoring */}
        <div className="team-scoring-section">
          <h2 className="team-name">{currentTeam.name}</h2>
          <div className="team-points">
            Totaal: <strong>{calculateTeamPoints(currentTeam.id)} punten</strong>
            <span className="max-points">/ {playedSongs.length * 3} max</span>
          </div>

          <div className="songs-scoring-grid">
            <div className="grid-header">
              <span className="col-num">#</span>
              <span className="col-year">Jaar</span>
              <span className="col-artist">Artiest</span>
              <span className="col-title">Titel</span>
              <span className="col-all">Alle 3</span>
            </div>

            {playedSongs.map((song, index) => {
              const songScore = scores[currentTeam.id]?.[song.id] || {
                yearCorrect: false,
                artistCorrect: false,
                titleCorrect: false,
              };
              const allCorrect = songScore.yearCorrect && songScore.artistCorrect && songScore.titleCorrect;

              return (
                <div key={song.id} className="song-row">
                  <div className="col-num">
                    <span className="song-number">{index + 1}</span>
                  </div>
                  <div className="col-year answer-col">
                    <div className="answer-text">{song.year}</div>
                    <label className="checkbox-cell">
                      <input
                        type="checkbox"
                        checked={songScore.yearCorrect}
                        onChange={() => toggleScore(currentTeam.id, song.id, 'yearCorrect')}
                      />
                      <span className="checkmark">✓</span>
                    </label>
                  </div>
                  <div className="col-artist answer-col">
                    <div className="answer-text">{song.artist}</div>
                    <label className="checkbox-cell">
                      <input
                        type="checkbox"
                        checked={songScore.artistCorrect}
                        onChange={() => toggleScore(currentTeam.id, song.id, 'artistCorrect')}
                      />
                      <span className="checkmark">✓</span>
                    </label>
                  </div>
                  <div className="col-title answer-col">
                    <div className="answer-text">{song.title}</div>
                    <label className="checkbox-cell">
                      <input
                        type="checkbox"
                        checked={songScore.titleCorrect}
                        onChange={() => toggleScore(currentTeam.id, song.id, 'titleCorrect')}
                      />
                      <span className="checkmark">✓</span>
                    </label>
                  </div>
                  <button 
                    className={`col-all all-btn ${allCorrect ? 'all-checked' : ''}`}
                    onClick={() => toggleAllCorrect(currentTeam.id, song.id)}
                  >
                    {allCorrect ? '✓✓✓' : '○○○'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team overview sidebar */}
        <div className="teams-overview">
          <h3>📋 Overzicht Alle Teams</h3>
          <div className="teams-list">
            {teams.map((team, index) => (
              <div 
                key={team.id}
                className={`team-overview-item ${index === currentTeamIndex ? 'active' : ''}`}
                onClick={() => setCurrentTeamIndex(index)}
              >
                <span className="team-overview-name">{team.name}</span>
                <span className="team-overview-points">
                  {calculateTeamPoints(team.id)} pts
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="score-entry-actions">
          <Button variant="secondary" onClick={() => navigate('/admin')}>
            Annuleren
          </Button>
          <Button variant="primary" onClick={handleSave}>
            💾 Scores Opslaan
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScoreEntry;
